import React, { useState } from 'react';
import { Box, Typography, Button, Divider, FormControlLabel, Switch, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import CustomStepper from '.';

const StepperShowcase = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [orientation, setOrientation] = useState('horizontal');
  const [size, setSize] = useState('medium');
  const [variant, setVariant] = useState('default');
  const [connectorStyle, setConnectorStyle] = useState('solid');
  const [showSubLabels, setShowSubLabels] = useState(false);
  const [allowClickNavigation, setAllowClickNavigation] = useState(true);

  const basicSteps = [
    { id: 1, label: 'Personal Info', subLabel: 'Create your profile', },
    { id: 2, label: 'Address', subLabel: 'Choose your items', },
    { id: 3, label: 'Payment', subLabel: 'Secure checkout', },
    { id: 4, label: 'Confirmation', subLabel: 'All done!', },
  ];

  const handleStepClick = (stepIndex, step) => {
    if (!step.disabled && !step.error) {
      setActiveStep(stepIndex);
    }
  };    

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, basicSteps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  return (
    <Box sx={{ }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 600 }}>
        Custom Stepper Component Showcase
      </Typography>

      {/* Controls Panel */}
      <Box sx={{ 
        p: 3, 
        border: '1px solid #e0e0e0', 
        borderRadius: 2, 
        mb: 4,
        backgroundColor: '#f9f9f9'
      }}>
        <Typography variant="h6" gutterBottom>
          Configuration Controls
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Orientation</InputLabel>
            <Select
              value={orientation}
              label="Orientation"
              onChange={(e) => setOrientation(e.target.value)}
            >
              <MenuItem value="horizontal">Horizontal</MenuItem>
              <MenuItem value="vertical">Vertical</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Size</InputLabel>
            <Select
              value={size}
              label="Size"
              onChange={(e) => setSize(e.target.value)}
            >
              <MenuItem value="small">Small</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="large">Large</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Variant</InputLabel>
            <Select
              value={variant}
              label="Variant"
              onChange={(e) => setVariant(e.target.value)}
            >
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="outlined">Outlined</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Connector</InputLabel>
            <Select
              value={connectorStyle}
              label="Connector"
              onChange={(e) => setConnectorStyle(e.target.value)}
            >
              <MenuItem value="solid">Solid</MenuItem>
              <MenuItem value="dashed">Dashed</MenuItem>
              <MenuItem value="thick">Thick</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={showSubLabels}
                onChange={(e) => setShowSubLabels(e.target.checked)}
              />
            }
            label="Sub Labels"
          />

          <FormControlLabel
            control={
              <Switch
                checked={allowClickNavigation}
                onChange={(e) => setAllowClickNavigation(e.target.checked)}
              />
            }
            label="Click Navigation"
          />
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button onClick={handleBack} disabled={activeStep === 0} size="small">
            Previous
          </Button>
          <Button onClick={handleNext} disabled={activeStep === basicSteps.length - 1} size="small">
            Next
          </Button>
          <Button onClick={handleReset} size="small" variant="outlined">
            Reset
          </Button>
        </Box>
      </Box>

      {/* Interactive Demo */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Interactive Demo
        </Typography>
        
        <Box sx={{ 
          p: 3, 
          border: '1px solid #e0e0e0', 
          borderRadius: 2,
          minHeight: orientation === 'vertical' ? 300 : 'auto'
        }}>
          <CustomStepper
            steps={basicSteps}
            alignment={'center'}
            activeStep={activeStep}
            orientation={orientation}
            size={size}
            variant={variant}
            connectorStyle={connectorStyle}
            showSubLabels={showSubLabels}
            allowClickNavigation={allowClickNavigation}
            onStepClick={handleStepClick}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Preset Variants */}
      <Typography variant="h2" gutterBottom sx={{ mb: 3 }}>
        Preset Variants
      </Typography>

      {/* Basic Horizontal Stepper */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Basic Horizontal Steppers Center Text
        </Typography>
        <Box sx={{ p: 3, border: '1pzx solid #e0e0e0', borderRadius: 2 }}>
          <CustomStepper
            type = "horizontal-center"
            orientation='horizontal'
            steps={basicSteps}
            activeStep={1}
            allowClickNavigation={true}
            onStepClick={(index) => console.log('Clicked step:', index)}
            showSubLabels={false}
            alignment="center"
          />
        </Box>
        <Box sx={{ p: 3, border: '1pzx solid #e0e0e0', borderRadius: 2 }}>
          <CustomStepper
            type = "horizontal-center"
            orientation='horizontal'
            steps={basicSteps}
            activeStep={1}
            allowClickNavigation={true}
            onStepClick={(index) => console.log('Clicked step:', index)}
            showSubLabels={true}
            alignment="center"
          />
        </Box>
      </Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Basic Horizontal Steppers with horizontal text
        </Typography>
        <Box sx={{ p: 3, border: '1pzx solid #e0e0e0', borderRadius: 2 }}>
          <CustomStepper
            type = "horizontal-left"
            orientation='horizontal'
            steps={basicSteps}
            activeStep={1}
            allowClickNavigation={true}
            onStepClick={(index) => console.log('Clicked step:', index)}
            showSubLabels={false}
            alignment="center"
          />
        </Box>
        <Box sx={{ p: 3, border: '1pzx solid #e0e0e0', borderRadius: 2 }}>
          <CustomStepper
            type = "horizontal-left"
            orientation='horizontal'
            steps={basicSteps}
            activeStep={1}
            allowClickNavigation={true}
            onStepClick={(index) => console.log('Clicked step:', index)}
            showSubLabels={true}
            alignment="center"
          />
        </Box>
      </Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Vertical Stepper center text
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ p: 3, border: '1pzx solid #e0e0e0', borderRadius: 2 }}>
            <CustomStepper
                type='vertical-left'
                orientation='vertical'
                steps={basicSteps}
                activeStep={1}
                allowClickNavigation={true}
                onStepClick={(index) => console.log('Clicked step:', index)}
                showSubLabels={false}
                alignment="center"
            />
            </Box>
            <Box sx={{ p: 3, border: '1pzx solid #e0e0e0', borderRadius: 2 }}>
            <CustomStepper
                type='vertical-left'
                orientation='vertical'
                steps={basicSteps}
                activeStep={1}
                allowClickNavigation={true}
                onStepClick={(index) => console.log('Clicked step:', index)}
                showSubLabels={true}
                alignment="center"
            />
            </Box>
        </Box>
        <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
            Vertical Stepper left text
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ p: 3, border: '1pzx solid #e0e0e0', borderRadius: 2 }}>
                    <CustomStepper
                        type='vertical-center'
                        orientation='vertical'
                        steps={basicSteps}
                        activeStep={1}
                        allowClickNavigation={true}
                        onStepClick={(index) => console.log('Clicked step:', index)}
                        showSubLabels={false}
                        alignment="center"
                    />
                </Box>
                <Box sx={{ p: 3, border: '1pzx solid #e0e0e0', borderRadius: 2 }}>
                    <CustomStepper
                        type='vertical-center'
                        orientation='vertical'
                        steps={basicSteps}
                        activeStep={1}
                        allowClickNavigation={true}
                        onStepClick={(index) => console.log('Clicked step:', index)}
                        showSubLabels={true}
                        alignment="center"
                    />
                </Box>
            </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StepperShowcase;
